import React, { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { MoldOrder, MoldOrderStatus } from '../../types/mold';
import { useAppState } from '../../context/AppStateContext';
import UserProfileImage from '../KanbanBoard/UserProfileImage';
import ApprovalSlot from './ApprovalSlot';
import { calculateDuration, formatShortDateTime } from '@/utils/dateUtils';

type SelectedStepDetailProps = {
  moldOrder: MoldOrder;
  selectedStepIndex: number;
};

const SelectedStepDetail: React.FC<SelectedStepDetailProps> = ({ moldOrder, selectedStepIndex }) => {
  const { getStatusText } = useAppState();

  const workflowStep = moldOrder.workflowStructure?.[selectedStepIndex];

  const displayStep = useMemo(() => {
    if (!workflowStep) return null;
    return { name: getStatusText(workflowStep.statusName as MoldOrderStatus, moldOrder.type) }
  }, [workflowStep, moldOrder.type]);

  const stateInstance = useMemo(() => {
    if (!moldOrder.stateInstances) return null;
    return moldOrder.stateInstances[selectedStepIndex];
  }, [moldOrder.stateInstances, selectedStepIndex]);

  if (!workflowStep) {
    return <div className="text-sm text-gray-500">ไม่มีข้อมูลขั้นตอนที่เลือก</div>;
  }

  return (
    <div className="bg-white border border-gray-100 rounded-lg p-3">
      <h4 className="text-sm font-semibold text-gray-800 mb-2">{displayStep?.name ?? workflowStep.statusName}</h4>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <UserProfileImage currentAssignee={stateInstance?.assignedTo ?? null} size="xs" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700 font-medium">{stateInstance?.assignedTo?.fullname ?? stateInstance?.assignedTo?.username ?? 'ยังไม่ระบุผู้รับผิดชอบ'}</p>
            <div className="text-xs text-gray-500">{displayStep?.name ?? workflowStep.statusName}</div>
          </div>

          <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
            <div>เริ่ม: {stateInstance?.startTime ? formatShortDateTime(stateInstance.startTime) : '-'}</div>
            <div>สิ้นสุด: {stateInstance?.endTime ? formatShortDateTime(stateInstance.endTime) : '-'}</div>
            {stateInstance?.startTime && stateInstance?.endTime && <div className="inline-flex items-center text-gray-600">
              <Clock className="w-4 h-4 mr-1" />
              <span className="text-xs">{calculateDuration(stateInstance.startTime, stateInstance.endTime)}</span>
            </div>}
          </div>

          { stateInstance?.notes && (
            <div className="mt-3 bg-gray-50 p-3 rounded text-sm text-gray-700 border border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">หมายเหตุ</span>
                <span className="text-xs text-gray-500">{formatShortDateTime(stateInstance?.createdAt ?? stateInstance?.updatedAt ?? stateInstance?.startTime)}</span>
              </div>
              <p className="mt-2 leading-relaxed">{stateInstance?.notes ?? '-'}</p>
              <p className="mt-2 text-xs text-gray-500">โดย <span className="font-medium text-gray-700">{stateInstance?.assignedTo?.fullname ?? stateInstance?.assignedTo?.username ?? 'ไม่ระบุ'}</span></p>
            </div>
          )}
          {stateInstance?.approverSlots && stateInstance?.approverSlots.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-lg font-semibold text-gray-800 mt-4 mb-2">รายการอนุมัติ</h5>
              {stateInstance.approverSlots?.map((slot, index) => {
                return (
                  <ApprovalSlot
                    key={slot.id}
                    slot={slot}
                    index={index}
                    isCurrentSlot={false}
                    isCurrentUserSlot={false}
                    displayMode="readonly"
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectedStepDetail;
